// Reads an OSSE register CSV export and turns it into forms, questions and answers.
// Schema-driven (ported from the dashboard-de.html prototype): forms and questions
// come from the export's header rows, nothing names a specific question.

export const MISSING = "∅"; // "no answer" value in filters

// Answers that mean "no information" — real selectable options in OSSE, but they
// must not make a numeric/date question look like free text.
const INFO_MARKERS = new Set(["keine informationen verfügbar", "k.a.", "keine angabe", "n/a", "na", "-"]);
export const isInfoMarker = (v) => v != null && INFO_MARKERS.has(v.toLowerCase());

export const clean = (s) => String(s ?? "").replace(/\s+/g, " ").trim();

// ── CSV ─────────────────────────────────────────────────────────────────────
function detectDelimiter(text) {
  const nl = text.indexOf("\n");
  const first = text.slice(0, nl > 0 ? nl : 2000);
  return [";", ",", "\t"]
    .map((d) => [d, first.split(d).length])
    .sort((a, b) => b[1] - a[1])[0][0];
}

export function parseCSV(text) {
  if (text.charCodeAt(0) === 0xfeff) text = text.slice(1);
  const d = detectDelimiter(text);
  const rows = [];
  let row = [], f = "", q = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (q) {
      if (c === '"') { if (text[i + 1] === '"') { f += '"'; i++; } else q = false; }
      else f += c;
    } else if (c === '"') q = true;
    else if (c === d) { row.push(f); f = ""; }
    else if (c === "\n") { row.push(f); rows.push(row); row = []; f = ""; }
    else if (c !== "\r") f += c;
  }
  if (f !== "" || row.length) { row.push(f); rows.push(row); }
  return rows;
}

// ── Value parsing ───────────────────────────────────────────────────────────
const reDE = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/;
const reISO = /^(\d{4})-(\d{2})-(\d{2})(?:[ T].*)?$/;
const reNum = /^-?\d+(?:[.,]\d+)?$/;

export function toISO(v) {
  if (v == null) return null;
  let m = reDE.exec(v);
  if (m) return `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;
  m = reISO.exec(v);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  return null;
}
export const toNum = (v) => (v != null && reNum.test(v) ? parseFloat(v.replace(",", ".")) : null);

function inferType(f) {
  const answered = f.raw.filter((x) => x != null);
  if (!answered.length) return "empty";
  const v = answered.filter((x) => !isInfoMarker(x));
  if (!v.length) return "categorical";
  const distinct = new Set(v).size;
  if (v.filter((x) => toISO(x)).length / v.length >= 0.9) return "date";
  const nums = v.map(toNum).filter((x) => x != null);
  if (nums.length / v.length >= 0.9) {
    const allInt = nums.every(Number.isInteger);
    return !allInt || distinct > 10 ? "numeric" : "categorical";
  }
  const avgLen = v.reduce((a, x) => a + x.length, 0) / v.length;
  if (distinct <= 20 && avgLen <= 60) return "categorical";
  if (distinct / v.length <= 0.3 && avgLen <= 40) return "categorical";
  return "text";
}

// German names for the platform's standard identifier columns
const ID_DE = {
  "pid": "PID", "patient repeat number": "Laufende Patientennummer", "episode number": "Episodennummer",
  "location id": "Standort-ID", "location": "Standort", "episode date": "Episodendatum",
  "episode description": "Episodenbeschreibung", "patient id": "Patienten-ID",
};

// ── Schema discovery ────────────────────────────────────────────────────────
export function buildDataset(rows) {
  // OSSE exports have a label column ("Form ID:", "Form name:", …) above the real header row
  let labelCol = -1;
  for (let r = 0; r < Math.min(25, rows.length) && labelCol < 0; r++) {
    labelCol = rows[r].findIndex((v) => /^form id:?$/i.test(clean(v)));
  }
  const plain = labelCol < 0;

  let headerRow = 0;
  const meta = {};
  if (!plain) {
    headerRow = -1;
    for (let r = 0; r < rows.length; r++) {
      const lab = clean(rows[r][labelCol]).toLowerCase().replace(/:$/, "");
      if (lab.startsWith("unique id")) { headerRow = r; break; }
      if (lab) meta[lab] = rows[r];
    }
    if (headerRow < 0) headerRow = Object.keys(meta).length;
  }

  const H = rows[headerRow] || [];
  const M = (name) => meta[name] || [];
  const formIdRow = M("form id"), formNameRow = M("form name");
  const deRow = M("data element id"), desRow = M("data element designation"), defRow = M("data element definition");
  const data = rows.slice(headerRow + 1).filter((r) => r.some((v) => clean(v) !== ""));
  const ncols = Math.max(H.length, ...data.map((r) => r.length));

  const groups = [], gById = {}, fields = [], byKey = {};
  const group = (id, name) => {
    if (!gById[id]) { gById[id] = { id, name, fields: [], status: null }; groups.push(gById[id]); }
    return gById[id];
  };
  const idGroup = group("_id", plain ? "Spalten" : "Patient & Episode");

  for (let c = 0; c < ncols; c++) {
    if (c === labelCol) continue;
    const header = clean(H[c]);
    let g, key, label, role = "q";
    if (plain || c < labelCol) {
      if (!header) continue;
      g = idGroup; key = "id:" + header; label = header; role = "id";
    } else {
      const fRaw = clean(formIdRow[c]);
      if (!header && !fRaw) continue;
      const hm = header.match(/^(?:FormStatus|X)_(\d+)/i);
      const formId = fRaw.replace(/_ver-.*$/i, "") || (hm ? "form_" + hm[1] : "other");
      g = group(formId, clean(formNameRow[c]) || formId);
      const elementId = clean(deRow[c]).replace(/:(\*|\d+)$/, "");
      const isStatus = /^formstatus/i.test(header) || (!elementId && !clean(desRow[c]) && !/^X_/i.test(header));
      role = isStatus ? "status" : "q";
      key = isStatus ? formId + "|status" : formId + "|" + (elementId || header);
      label = isStatus ? "Formularstatus" : (clean(desRow[c]) || clean(defRow[c]) || header);
    }
    if (byKey[key]) { byKey[key].cols.push(c); continue; } // same question in several form versions
    const f = { key, group: g.id, role, label, cols: [c] };
    byKey[key] = f; fields.push(f); g.fields.push(f);
    if (role === "status") g.status = f;
  }

  const n = data.length;
  for (const f of fields) {
    f.raw = data.map((row) => {
      for (const c of f.cols) { const v = clean(row[c]); if (v) return v; }
      return null;
    });
    f.num = f.raw.map((v) => (isInfoMarker(v) ? null : toNum(v)));
    f.iso = f.raw.map((v) => (isInfoMarker(v) ? null : toISO(v)));
    f.answered = f.raw.filter((v) => v != null).length;
    f.type = f.role === "status" ? "status" : inferType(f);
  }

  // Identifier columns
  const idFields = idGroup.fields;
  const find = (re) => idFields.find((f) => re.test(f.label));
  const patient = find(/^(pid|patient ?id|patient)$/i) || (plain ? null : idFields[0]);
  const location = find(/^(location|standort|site|zentrum)$/i) || find(/location(?! ?id)|standort/i);
  const episode = find(/^episode (number|nr|no)/i) || find(/^episode$/i);
  for (const f of idFields) { const de = ID_DE[f.label.toLowerCase()]; if (de) f.label = de; }

  const pid = [];
  for (let i = 0; i < n; i++) pid.push(patient && patient.raw[i] != null ? patient.raw[i] : "#row" + i);

  // Episodes = rows with the same patient + episode number. OSSE writes one row per
  // selected value of a multiple-choice answer, so one episode can span several rows.
  const episodes = [], epByKey = new Map(), episodesOf = new Map();
  for (let i = 0; i < n; i++) {
    const k = episode && episode.raw[i] != null ? pid[i] + "\u0001" + episode.raw[i] : pid[i] + "\u0001#" + i;
    let e = epByKey.get(k);
    if (!e) {
      e = { pid: pid[i], rows: [], rank: (episode && episode.num[i] != null ? episode.num[i] : 0) * 1e6 + i };
      epByKey.set(k, e); episodes.push(e);
      if (!episodesOf.has(e.pid)) episodesOf.set(e.pid, []);
      episodesOf.get(e.pid).push(e);
    }
    e.rows.push(i);
  }
  for (const list of episodesOf.values()) list.sort((a, b) => a.rank - b.rank);

  // A question is multiple choice when one episode holds several different answers
  for (const f of fields) f.multi = episodes.some((e) => valuesOf(f, e.rows).length > 1);

  // A form is "patient-level" when it is filled in once per patient and repeated in
  // every episode (e.g. Erstkontakt), "episode-level" when it is filled in again per
  // episode (e.g. Kontrolle). Multiple-choice answers are ignored for this check:
  // OSSE spreads their values over the rows, so they differ between episodes anyway.
  const hasData = (g, rows) => g.fields.some((f) => rows.some((i) => f.raw[i] != null));
  for (const g of groups) {
    if (g.id === "_id") continue;
    const single = g.fields.filter((f) => f.role === "q" && !f.multi);
    const sig = (e) => single.map((f) => valuesOf(f, e.rows)[0] ?? "").join("\u0001");
    g.level = "patient";
    for (const list of episodesOf.values()) {
      const withData = list.filter((e) => hasData(g, e.rows));
      if (withData.length > 1 && withData.some((e) => sig(e) !== sig(withData[0]))) { g.level = "episode"; break; }
    }
  }

  // Filled-in forms ("records") per patient, oldest first. A record is the set of
  // CSV rows that belong to one form: all rows of the patient for patient-level
  // forms, the rows of one episode for episode-level forms and the identifier columns.
  for (const g of groups) {
    g.records = new Map();
    for (const [p, list] of episodesOf) {
      let recs;
      if (g.id === "_id") recs = list.map((e) => ({ pid: p, rows: e.rows, rank: e.rank }));
      else {
        const withData = list.filter((e) => hasData(g, e.rows));
        recs = g.level === "patient"
          ? (withData.length ? [{ pid: p, rows: withData.flatMap((e) => e.rows), rank: withData[withData.length - 1].rank }] : [])
          : withData.map((e) => ({ pid: p, rows: e.rows, rank: e.rank }));
      }
      if (recs.length) g.records.set(p, recs);
    }
  }

  return {
    n, fields, byKey, groups, pid, patients: [...episodesOf.keys()], episodes,
    patient, location, episode, hasData, plain,
  };
}

// Distinct answers of question f within the given CSV rows, in export order
export function valuesOf(f, rows) {
  const out = [];
  for (const i of rows) { const v = f.raw[i]; if (v != null && !out.includes(v)) out.push(v); }
  return out;
}

// Newest filled-in form groupId of patient p ({ rows, rank }), null = not filled in
export function recordFor(ds, groupId, p) {
  const recs = ds.groups.find((g) => g.id === groupId).records.get(p);
  return recs ? recs[recs.length - 1] : null;
}

// First answer of question f for patient p, as used by filters
export function valueFor(ds, f, p) {
  const r = recordFor(ds, f.group, p);
  return (r && valuesOf(f, r.rows)[0]) ?? MISSING;
}
